import { Fragment, useEffect, useState } from 'react';
import { useAuth } from '../../../auth';
import type {
  AccountDeletionAffectedRecords,
  AccountDeletionRequestStatus,
  AdminAccountDeletionRequest,
} from '@modelcast/types';

type StatusFilter = AccountDeletionRequestStatus;

const FILTERS: { status: StatusFilter; label: string }[] = [
  { status: 'pending', label: 'Pending' },
  { status: 'approved', label: 'Approved' },
  { status: 'rejected', label: 'Rejected' },
];

const NOTES_LIMIT = 2000;
const PAGE_SIZE = 50;

function formatDate(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function statusClass(status: AccountDeletionRequestStatus) {
  if (status === 'pending') return 'bg-warn/20 text-warn';
  if (status === 'approved') return 'bg-ok/20 text-ok';
  if (status === 'rejected') return 'bg-red-500/20 text-red-500';
  return 'bg-panel2 text-text-dim';
}

function executionLabel(request: AdminAccountDeletionRequest) {
  if (request.executionStatus === 'completed') return 'Completed';
  if (request.lastExecutionError) return 'Failed — retryable';
  if (request.executionStatus === 'not_started' || !request.executionStatus) return 'Not executed';
  return `Checkpoint: ${request.executionStatus.replace(/_/g, ' ')}`;
}

function executionClass(request: AdminAccountDeletionRequest) {
  if (request.executionStatus === 'completed') return 'bg-ok/20 text-ok';
  if (request.lastExecutionError) return 'bg-red-500/20 text-red-500';
  return 'bg-panel2 text-text-dim';
}

function profileLabel(request: AdminAccountDeletionRequest) {
  return request.profile.displayName ?? request.profile.username ?? request.userId;
}

function notesSummary(value: string | null) {
  if (!value) return '-';
  return value.length > 120 ? `${value.slice(0, 117)}...` : value;
}

export function AccountDeletionRequestsPage() {
  const { getToken } = useAuth();
  const [requests, setRequests] = useState<AdminAccountDeletionRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notesDrafts, setNotesDrafts] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  // Execute-confirmation panel (one open at a time).
  const [executePanelId, setExecutePanelId] = useState<string | null>(null);
  const [executeNote, setExecuteNote] = useState('');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [affected, setAffected] = useState<AccountDeletionAffectedRecords | null>(null);
  const [affectedError, setAffectedError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    void fetchRequests();
  }, [status, page]);

  async function fetchRequests(existingToken?: string | null) {
    setIsLoading(true);
    setError(null);
    try {
      const token = existingToken ?? await getToken();
      const params = new URLSearchParams({
        status,
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      const response = await fetch(`/api/admin/account-deletion-requests?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load account deletion requests');
      }
      const data = await response.json();
      const nextRequests: AdminAccountDeletionRequest[] = data.requests || [];
      setRequests(nextRequests);
      setTotal(data.total || 0);
      setNotesDrafts((current) => {
        const next = { ...current };
        for (const request of nextRequests) {
          if (!next[request.id]) next[request.id] = '';
        }
        return next;
      });
    } catch (err) {
      console.error('Error fetching account deletion requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load account deletion requests');
    } finally {
      setIsLoading(false);
    }
  }

  function selectStatus(nextStatus: StatusFilter) {
    setStatus(nextStatus);
    setPage(0);
    setRowErrors({});
    setNotesDrafts({});
    closeExecutePanel();
  }

  function updateNotes(requestId: string, value: string) {
    setNotesDrafts((current) => ({ ...current, [requestId]: value }));
    setRowErrors((current) => ({ ...current, [requestId]: '' }));
  }

  async function resolveRequest(request: AdminAccountDeletionRequest, nextStatus: Exclude<AccountDeletionRequestStatus, 'pending'>) {
    const notes = notesDrafts[request.id]?.trim() ?? '';
    if (!notes) {
      setRowErrors((current) => ({
        ...current,
        [request.id]: nextStatus === 'approved'
          ? 'Add notes for manual processing before approving.'
          : 'Add the policy or legal reason before rejecting.',
      }));
      return;
    }

    setSubmittingId(request.id);
    setRowErrors((current) => ({ ...current, [request.id]: '' }));
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/account-deletion-requests/${request.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: nextStatus,
          adminNotes: notes,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to resolve request');
      }
      setNotesDrafts((current) => ({ ...current, [request.id]: '' }));
      await fetchRequests(token);
    } catch (err) {
      console.error('Error resolving account deletion request:', err);
      setRowErrors((current) => ({
        ...current,
        [request.id]: err instanceof Error ? err.message : 'Failed to resolve request',
      }));
    } finally {
      setSubmittingId(null);
    }
  }

  function closeExecutePanel() {
    setExecutePanelId(null);
    setExecuteNote('');
    setExecuteError(null);
    setAffected(null);
    setAffectedError(null);
  }

  async function openExecutePanel(request: AdminAccountDeletionRequest) {
    setExecutePanelId(request.id);
    setExecuteNote('');
    setExecuteError(null);
    setAffected(null);
    setAffectedError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/account-deletion-requests/${request.id}/affected-records`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load affected records');
      }
      const data = await response.json();
      setAffected(data.summary ?? null);
    } catch (err) {
      console.error('Error fetching affected records:', err);
      setAffectedError(err instanceof Error ? err.message : 'Failed to load affected records');
    }
  }

  async function executeRequest(request: AdminAccountDeletionRequest) {
    const note = executeNote.trim();
    if (!note) {
      setExecuteError('An audit note is required before executing.');
      return;
    }

    setIsExecuting(true);
    setExecuteError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/account-deletion-requests/${request.id}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ auditNote: note }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to execute account deletion');
      }
      closeExecutePanel();
      await fetchRequests(token);
    } catch (err) {
      console.error('Error executing account deletion request:', err);
      setExecuteError(err instanceof Error ? err.message : 'Failed to execute account deletion');
      // The failure checkpoint is recorded server-side; refresh so the row
      // shows the retryable failed state.
      await fetchRequests().catch(() => undefined);
      setExecutePanelId(request.id);
    } finally {
      setIsExecuting(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Account Deletion</h1>
          <p className="mt-1 text-sm text-text-dim">
            Review self-serve account deletion requests, then execute approved ones. Execution anonymizes the public profile and removes the sign-in; financial records are retained for 7 years.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.status}
            type="button"
            onClick={() => selectStatus(filter.status)}
            className={`btn btn-sm ${status === filter.status ? 'btn-accent' : ''}`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-line bg-panel p-8 text-center">
          <div className="text-text-dim">No {status} account deletion requests</div>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-line bg-panel">
            <table className="w-full">
              <thead className="border-b border-line bg-panel2">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">User</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Requested</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Age</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Reviewed</th>
                  <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Admin Notes</th>
                  <th className="px-4 py-3 text-right text-[10px] font-mono uppercase text-text-faint">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => {
                  const label = profileLabel(request);
                  const notes = notesDrafts[request.id] ?? '';
                  const remaining = NOTES_LIMIT - notes.length;
                  const rowSubmitting = submittingId === request.id;
                  const isApproved = request.status === 'approved';
                  const isExecuted = request.executionStatus === 'completed';
                  const executePanelOpen = executePanelId === request.id;

                  return (
                    <Fragment key={request.id}>
                    <tr className="border-b border-line last:border-0 align-top">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text">{label}</div>
                        <div className="mt-0.5 text-xs text-text-dim">{request.userId}</div>
                        {!request.profile.id && (
                          <div className="mt-1 text-xs text-text-faint">No profile on file</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] ${statusClass(request.status)}`}>
                            {request.status}
                          </span>
                          {isApproved && (
                            <span className={`rounded px-1.5 py-0.5 text-[10px] ${executionClass(request)}`}>
                              {executionLabel(request)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-dim">{formatDate(request.requestedAt)}</td>
                      <td className="px-4 py-3 text-sm text-text-dim">{request.ageDays}d</td>
                      <td className="px-4 py-3 text-sm text-text-dim">{formatDate(request.resolvedAt)}</td>
                      <td className="max-w-[280px] px-4 py-3 text-sm text-text-dim">
                        {request.status === 'pending' ? (
                          <div className="flex flex-col gap-1">
                            <label htmlFor={`admin-notes-${request.id}`} className="sr-only">
                              Admin notes for {label}
                            </label>
                            <textarea
                              id={`admin-notes-${request.id}`}
                              value={notes}
                              onChange={(event) => updateNotes(request.id, event.target.value)}
                              maxLength={NOTES_LIMIT}
                              rows={3}
                              className="input min-w-[220px] resize-none pt-2 text-sm"
                              placeholder="Manual processing or rejection reason"
                              disabled={rowSubmitting}
                            />
                            {remaining <= 200 && (
                              <span className="text-xs text-text-faint">{remaining} characters remaining</span>
                            )}
                            {rowErrors[request.id] && (
                              <span className="text-xs text-red-700">{rowErrors[request.id]}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span title={request.adminNotes ?? undefined}>{notesSummary(request.adminNotes)}</span>
                            {request.lastExecutionError && !isExecuted && (
                              <span className="text-xs text-red-700" title={request.lastExecutionError}>
                                Last execution error: {notesSummary(request.lastExecutionError)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {request.status === 'pending' ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => resolveRequest(request, 'approved')}
                              disabled={rowSubmitting}
                              className="btn btn-sm disabled:opacity-50"
                              aria-label={`Approve account deletion request from ${label}`}
                              aria-busy={rowSubmitting}
                            >
                              {rowSubmitting ? 'Saving...' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              onClick={() => resolveRequest(request, 'rejected')}
                              disabled={rowSubmitting}
                              className="btn btn-sm disabled:opacity-50"
                              aria-label={`Reject account deletion request from ${label}`}
                              aria-busy={rowSubmitting}
                            >
                              Reject
                            </button>
                          </div>
                        ) : isApproved && !isExecuted ? (
                          <button
                            type="button"
                            onClick={() => executePanelOpen ? closeExecutePanel() : void openExecutePanel(request)}
                            disabled={isExecuting}
                            className="btn btn-sm disabled:opacity-50"
                            aria-label={`${request.lastExecutionError ? 'Retry' : 'Execute'} account deletion for ${label}`}
                          >
                            {request.lastExecutionError ? 'Retry execution' : 'Execute deletion'}
                          </button>
                        ) : isExecuted ? (
                          <span className="text-xs text-text-faint">Executed {formatDate(request.executedAt)}</span>
                        ) : (
                          <span className="text-xs text-text-faint">Resolved</span>
                        )}
                      </td>
                    </tr>
                    {executePanelOpen && (
                      <tr className="border-b border-line last:border-0 bg-panel2/40">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="rounded-lg border border-warn/40 bg-warn/10 p-4">
                            <h3 className="text-sm font-medium text-text">
                              {request.lastExecutionError ? 'Retry account deletion execution' : 'Execute account deletion'} for {label}
                            </h3>
                            <p className="mt-2 text-sm text-text-dim">
                              This anonymizes the public profile and session content attribution, then permanently deletes the sign-in. Financial records below are retained for 7 years and only decoupled from public identity. Buyers keep packs they purchased from this user.
                            </p>

                            {request.lastExecutionError && (
                              <p className="mt-3 text-sm text-red-700">
                                Previous attempt failed and is resumable from checkpoint &quot;{request.executionStatus.replace(/_/g, ' ')}&quot;: {request.lastExecutionError}
                              </p>
                            )}

                            <div className="mt-3">
                              {affectedError ? (
                                <p className="text-sm text-red-700">{affectedError}</p>
                              ) : !affected ? (
                                <p className="text-sm text-text-dim">Loading affected records...</p>
                              ) : (
                                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" data-testid="deletion-affected-records">
                                  <div>
                                    <dt className="label-mono">Payments</dt>
                                    <dd className="mt-0.5 text-text">{affected.payments}</dd>
                                  </div>
                                  <div>
                                    <dt className="label-mono">Payouts</dt>
                                    <dd className="mt-0.5 text-text">{affected.payouts}</dd>
                                  </div>
                                  <div>
                                    <dt className="label-mono">Pack purchases</dt>
                                    <dd className="mt-0.5 text-text">{affected.productPurchases}</dd>
                                  </div>
                                  <div>
                                    <dt className="label-mono">Packs listed</dt>
                                    <dd className="mt-0.5 text-text">{affected.productsListed}</dd>
                                  </div>
                                </dl>
                              )}
                            </div>

                            <div className="mt-4 flex flex-col gap-2">
                              <label htmlFor={`execute-note-${request.id}`} className="label-mono">
                                Audit note <span className="font-normal text-text-faint">(required)</span>
                              </label>
                              <textarea
                                id={`execute-note-${request.id}`}
                                value={executeNote}
                                onChange={(event) => {
                                  setExecuteNote(event.target.value);
                                  setExecuteError(null);
                                }}
                                maxLength={NOTES_LIMIT}
                                rows={2}
                                className="input resize-none pt-2 text-sm"
                                placeholder="Why this deletion is being executed now"
                                disabled={isExecuting}
                              />
                            </div>

                            {executeError && <p className="mt-3 text-sm text-red-700">{executeError}</p>}

                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={closeExecutePanel}
                                disabled={isExecuting}
                                className="btn btn-sm disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => executeRequest(request)}
                                disabled={isExecuting}
                                className="btn btn-sm btn-accent disabled:opacity-50"
                                aria-busy={isExecuting}
                              >
                                {isExecuting ? 'Executing...' : 'Confirm and execute'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-text-dim">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((current) => current - 1)}
                  disabled={page === 0}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((current) => current + 1)}
                  disabled={page >= totalPages - 1}
                  className="btn btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
